import { useIonModal } from '@ionic/react'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useManagementGetNonAllocatedSims } from '@apps-common/apis/management/non-allocated-sim'
import {
  UnitsAddedModal,
  UnitSaveTimerModal,
  UnitStreamCheckView
} from '@apps-common/apps/portal/pages/units/pages/create-multiple/components/initial-config'
import { CardContainer } from '@apps-common/components/common'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundedSm, InputInfoSM, InputSM, InputSMDebounced } from '@apps-common/components/elements'
import { VertSpacerLg, VertSpacerXl } from '@apps-common/components/layout'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { CID_ACCOUNT_REGEX } from '@apps-common/config/regex'
import { SignalRProvider } from '@apps-common/contexts/SignalrContext'
import { useManagementUnitSelectItems } from '@apps-common/hooks/useManagementUnitSelectItems'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import {
  modalFadeInEnterAnimation,
  modalFadeInLeaveAnimation,
  modalSlideUpEnterAnimation,
  modalSlideUpLeaveAnimation
} from '@apps-common/lib/modal-animations'
import { toastError, toastSuccess, toastWarning } from '@apps-common/lib/toast'
import { IUnitManagementSelectOption } from '@apps-common/types/form'
import { INonAllocatedSimModel } from '@apps-common/types/management/non-allocated-sim'
import { IUnitCreateMultipleDefaultsModel, IUnitSimActiveModel } from '@apps-common/types/management/unit'

interface IProps {
  unitDefaults: IUnitCreateMultipleDefaultsModel
  restart: () => void
}

interface IUnitDetailsForm {
  simNo: string
  imei: string
  serialNo: string
  accountNo: string
}

export const UnitAddProcessing: React.FC<IProps> = ({ unitDefaults, restart }) => {
  const { navigateBack } = useNavigation()
  const { isLoading: selectItemsLoading, simApns, simTypes } = useManagementUnitSelectItems()
  const [addedDevices, setAddedDevices] = useState<IUnitSimActiveModel[]>([])

  const [trafficValidated, setTrafficValidated] = useState<boolean>()
  const [readyToSave, setReadyToSave] = useState<boolean>()

  const [nonAllocatedSim, setNonAllocatedSim] = useState<INonAllocatedSimModel | null>(null)
  const [nonAllocatedSimApn, setNonAllocatedSimApn] = useState<IUnitManagementSelectOption | null>(null)

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    setValue,
    watch,
    setFocus,
    reset
  } = useForm<IUnitDetailsForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      simNo: '',
      imei: '',
      serialNo: '',
      accountNo: ''
    }
  })

  const simNoVal = watch('simNo')
  const imeiVal = watch('imei')
  const serialNoVal = watch('serialNo')
  const accountNoVal = watch('accountNo')
  const accountNoRequired = !unitDefaults?.accountNo || unitDefaults?.accountNo?.length === 0
  const simNoEntered = !!simNoVal && simNoVal.length >= 3

  const {
    data: sims,
    isLoading: simsIsLoading,
    isError: simsIsError
  } = useManagementGetNonAllocatedSims({
    config: {
      staleTime: 1,
      gcTime: 1,
      enabled: simNoEntered
    },
    search: simNoVal
  })

  const clearNonAllocatedSim = () => {
    setNonAllocatedSim(null)
    setNonAllocatedSimApn(null)
    setValue('simNo', '', { shouldValidate: false })
    setTimeout(() => {
      setFocus('simNo')
    }, 1000)
  }

  const simsLoading = simsIsLoading || simsIsError
  const imeiValid = useMemo(() => imeiVal && imeiVal.length >= 4, [imeiVal])
  const serialNoValid = useMemo(() => serialNoVal && serialNoVal.length >= 4, [serialNoVal])
  const accountNoValid = useMemo(
    () => !accountNoRequired || (!!accountNoVal && accountNoVal.length >= 4),
    [accountNoVal, accountNoRequired]
  )

  const hasAllValues = useMemo(() => {
    return nonAllocatedSim && imeiValid && serialNoValid && accountNoValid
  }, [nonAllocatedSim, imeiValid, serialNoValid, accountNoValid])

  const resetProcessing = () => {
    setReadyToSave(false)
    setTrafficValidated(false)

    reset({})
    setValue('accountNo', '', { shouldValidate: true })
    setValue('serialNo', '', { shouldValidate: true })
    setValue('imei', '', { shouldValidate: true })
    setValue('simNo', '', { shouldValidate: true })

    clearNonAllocatedSim()
  }

  const [present, dismiss] = useIonModal(UnitSaveTimerModal, {
    componentProps: {
      defaults: unitDefaults,
      extraDetails: {
        imei: imeiVal,
        serialNo: serialNoVal,
        accountNo: accountNoVal
      },
      nonAllocatedSim
    },
    onDismiss: (modalData: string, role: string) => {
      dismiss(modalData, role)
      if (role === ModalDismiss.Confirm) {
        try {
          const newUnit = JSON.parse(modalData)

          toastSuccess('New device added.')

          setAddedDevices((prev) => [...prev, newUnit])
          resetProcessing()
        } catch {
          toastError('Failed to add device. No new device returned after creation.')
        }
      }
    }
  })

  const saveDevice = () => {
    present({
      enterAnimation: modalFadeInEnterAnimation,
      leaveAnimation: modalFadeInLeaveAnimation
    })
  }

  useEffect(() => {
    if (readyToSave) {
      saveDevice()
    }
  }, [readyToSave])

  useEffect(() => {
    if (simsIsLoading || !simNoEntered) return

    if (sims && sims.length === 1) {
      const sim = sims[0]
      const apnItem = simApns.options.find((item) => item.value === sim.simApnId)

      setNonAllocatedSim(sim)
      setNonAllocatedSimApn(apnItem || null)

      return
    }

    if (!sims) {
      toastWarning('Non-allocated sims failed to load.')
    } else if (sims.length > 1) {
      toastWarning('More than 1 allocated sim found.')
    } else if (sims.length === 0) {
      toastWarning('No non-allocated sims found.')
    }
  }, [sims])

  useEffect(() => {
    if (simsIsLoading) return

    if (!nonAllocatedSim) {
      clearNonAllocatedSim()
    } else if (nonAllocatedSim && !imeiValid) {
      setFocus('imei')
    } else if (nonAllocatedSim && imeiValid && !serialNoValid) {
      setTimeout(() => {
        setFocus('serialNo')
      }, 1000)
    } else if (nonAllocatedSim && imeiValid && serialNoValid && !accountNoValid) {
      setTimeout(() => {
        setFocus('accountNo')
      }, 1000)
    }

    if (nonAllocatedSim && imeiValid && serialNoValid && accountNoValid && !unitDefaults.checkTrafficStream) {
      setTrafficValidated(true)
    }
  }, [nonAllocatedSim, imeiVal, serialNoVal, accountNoVal])

  useEffect(() => {
    if (trafficValidated) {
      setReadyToSave(true)
    }
  }, [trafficValidated])

  const loading = simsLoading || selectItemsLoading

  return (
    <div className='max-w-[1200px]'>
      <div className='flex items-center justify-start gap-[10px]'>
        <div className='flex items-center flex-wrap justify-center gap-2'>
          <ModalButton
            component={UnitsAddedModal}
            disabled={false}
            componentProps={{
              addedDevices
            }}
            classes='grow'
            enterAnimation={modalSlideUpEnterAnimation}
            leaveAnimation={modalSlideUpLeaveAnimation}
            backdropDismiss
          >
            <ButtonRoundedSm
              type='div'
              loading={loading}
              className='action-brand px-[10px]'
              icon={<commonIcons.stack className='text-[1.1em]' />}
              text={`${addedDevices.length || '0'} Added `}
            />
          </ModalButton>

          <ModalButton
            component={ConfirmModal}
            disabled={false}
            componentProps={{
              text: 'Restart',
              description:
                'Confirm you would restart the creation process? Any added devices will remain in the system, but cleared from the current session.'
            }}
            confirmCallbackAfterDismiss={() => restart()}
            classes='grow'
            enterAnimation={modalSlideUpEnterAnimation}
            leaveAnimation={modalSlideUpLeaveAnimation}
            backdropDismiss
          >
            <ButtonRoundedSm
              type='div'
              loading={loading}
              className='action-warning px-[10px]'
              icon={<commonIcons.refresh className='text-[1.1em]' />}
              text='Restart process'
            />
          </ModalButton>

          <ButtonRoundedSm
            type='button'
            loading={loading}
            className='action-warning px-[10px]'
            callback={() => resetProcessing()}
            icon={<commonIcons.reset className='text-[1.1em]' />}
            text='Reset current'
          />
        </div>
      </div>
      <VertSpacerLg />
      <div className='grid md:grid-cols-2 gap-[20px] max-w-[1200px]'>
        <div className=' grid gap-[20px] max-w-[600px]'>
          <CardContainer bgClasses='bg-surface border border-border text-text'>
            <SectionTitle text='Sim Card' classes='ml-[10px]' />
            <VertSpacerLg />
            <div className='bg-surface-2 border border-border rounded-3xl p-[10px] flex flex-col gap-2'>
              <InputSMDebounced
                type='text'
                placeholder='Enter Sim Number'
                label='Sim Number'
                id='simNumber'
                showLabel
                disabled={loading || !!nonAllocatedSim}
                registration={register('simNo', {
                  required: FORM_DEFAULT_REQUIRED
                })}
                errors={errors.simNo}
              />
            </div>
            {nonAllocatedSim && (
              <div className='border border-border text-text p-2 rounded-3xl mt-4 mb-2'>
                <div className='flex items-center justify-between mb-4 m-2'>
                  <SectionTitle text='Non-allocated sim' classes='!text-[1rem] bg-surface-2 px-2 rounded-full' />
                  <ButtonRoundedSm
                    type='button'
                    size='xs'
                    loading={loading}
                    className='action-warning px-2 py-[2px] !min-h-0 !h-auto !min-w-0 grow-0'
                    icon={<commonIcons.remove className='text-[1rem]' />}
                    text='Clear'
                    callback={() => resetProcessing()}
                  />
                </div>
                <div className='grid lg:grid-cols-2 gap-[10px]'>
                  <InputInfoSM
                    id='mobileNo'
                    showLabel
                    label='Mobile number'
                    value={nonAllocatedSim.mobileNo}
                    className='!bg-secondary-content/5'
                  />
                  <InputInfoSM
                    id='simNo'
                    showLabel
                    label='Sim card number'
                    value={nonAllocatedSim.simNo}
                    className='!bg-secondary-content/5'
                  />
                  <InputInfoSM id='ip' showLabel label='Sim IP address' value={nonAllocatedSim.ip} className='!bg-secondary-content/5' />
                  <InputInfoSM
                    id='simApn'
                    showLabel
                    label='Sim APN'
                    value={nonAllocatedSimApn?.label || 'Unknown'}
                    className='!bg-secondary-content/5'
                  />
                </div>
              </div>
            )}
          </CardContainer>
          <CardContainer bgClasses='bg-surface border border-border text-text'>
            <SectionTitle text='Device' />
            <VertSpacerLg />
            <div className='grid lg:grid-cols-2 lg:gap-[10px] bg-secondary-content/10 rounded-3xl p-[10px]'>
              <div className={classNames('relative', accountNoRequired && 'lg:col-span-2')}>
                <InputSMDebounced
                  type='text'
                  placeholder='Enter Device IMEI'
                  label='IMEI'
                  id='imei'
                  showLabel
                  disabled={loading || !nonAllocatedSim}
                  registration={register('imei', {
                    required: FORM_DEFAULT_REQUIRED
                  })}
                  errors={errors.imei}
                />
                {imeiVal && imeiVal.length > 0 && (
                  <button
                    type='button'
                    aria-label='Clear IMEI'
                    className='absolute right-[10px] top-[35px] p-[5px] bg-secondary-content/10 text-secondary-content rounded-full'
                    onClick={() => {
                      setValue('imei', '')
                    }}
                  >
                    <commonIcons.close />
                  </button>
                )}
              </div>
              <div className='relative'>
                <InputSMDebounced
                  type='text'
                  placeholder='Enter Device Serial'
                  label='Serial No'
                  id='serialNo'
                  showLabel
                  disabled={loading || !nonAllocatedSim}
                  registration={{
                    ...register('serialNo', {
                      required: FORM_DEFAULT_REQUIRED
                    }),
                    onChange: (e: any) => {
                      if (unitDefaults && unitDefaults?.serialNoPrefix?.length > 0) {
                        setValue('serialNo', e?.target?.value?.replace(unitDefaults.serialNoPrefix, '') || '', { shouldValidate: true })
                      } else {
                        setValue('serialNo', e.target.value, { shouldValidate: true })
                      }
                    }
                  }}
                  errors={errors.serialNo}
                />
                {serialNoVal && serialNoVal.length > 0 && (
                  <button
                    type='button'
                    aria-label='Clear SerialNo'
                    className='absolute right-[10px] top-[35px] p-[5px] bg-secondary-content/10 text-secondary-content rounded-full'
                    onClick={() => {
                      setValue('serialNo', '')
                    }}
                  >
                    <commonIcons.close />
                  </button>
                )}
              </div>
              {accountNoRequired && (
                <div>
                  <InputSM
                    type='text'
                    placeholder='CID account number'
                    label='CID account'
                    id='accountNo'
                    showLabel
                    disabled={loading || !nonAllocatedSim}
                    registration={register('accountNo', {
                      required: FORM_DEFAULT_REQUIRED,
                      pattern: {
                        value: CID_ACCOUNT_REGEX,
                        message: '4 numerical characters only e.g. 0123'
                      }
                    })}
                    errors={errors.accountNo}
                  />
                  {accountNoVal && accountNoVal.length > 0 && (
                    <button
                      type='button'
                      aria-label='Clear Account No'
                      className='absolute right-[10px] top-[35px] p-[5px] bg-secondary-content/10 text-secondary-content rounded-full'
                      onClick={() => {
                        setValue('accountNo', '')
                      }}
                    >
                      <commonIcons.close />
                    </button>
                  )}
                </div>
              )}
            </div>
          </CardContainer>
        </div>
        <SignalRProvider>
          {hasAllValues && unitDefaults.checkTrafficStream && isValid && (
            <UnitStreamCheckView ipAddress={nonAllocatedSim?.ip!} imei={imeiVal} onSuccess={() => setTrafficValidated(true)} />
          )}
        </SignalRProvider>
      </div>
      <VertSpacerXl />
      <div className='flex items-center justify-start gap-4'>
        <ModalButton
          component={ConfirmModal}
          disabled={false}
          componentProps={{
            text: 'Finish Create',
            description: 'Confirm you would like to finish creating devices? Any added devices will remain in the system.',
            action: ConfirmModalAction.Success
          }}
          confirmCallbackAfterDismiss={() => {
            navigateBack()
            restart()
          }}
          classes='grow sm:grow-0'
          enterAnimation={modalSlideUpEnterAnimation}
          leaveAnimation={modalSlideUpLeaveAnimation}
          backdropDismiss
        >
          <ButtonRoundedSm
            type='div'
            text='Finish'
            className='action-brand'
            loading={false}
            icon={<commonIcons.arrowAngled className='text-[1.1rem] rotate-[-90deg]' />}
          />
        </ModalButton>

        <ButtonRoundedSm
          type='button'
          text='Save'
          loading={true}
          className='action-success grow sm:grow-0'
          icon={<commonIcons.arrowRight className='text-[1.1rem]' />}
          iconRight
          callback={() => saveDevice()}
        />
      </div>
    </div>
  )
}
